import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBars,
  faSignOutAlt,
  faLocationDot,
  faFileInvoice,
  faUserPlus,
  faUser,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { APP_ROUTES } from '../config/api.config';
import Footer from '../components/Footer';
import { getProfile } from '../services/api';
import { useLogoutHandler } from '../utils/logoutHandler';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';
import Sidebar from '../components/Sidebar/Sidebar';
import theme from '../styles/theme';

const SIDEBAR_WIDTH = theme.layout.sidebarWidth;
const SIDEBAR_COLLAPSED = theme.layout.sidebarCollapsed;

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.colors.bgMesh};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MainContent = styled.div<{ isCollapsed: boolean }>`
  flex: 1;
  margin-left: ${props => (props.isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH)}px;
  padding: 0;
  padding-bottom: 60px;
  padding-top: ${theme.layout.topbarHeight + 12}px;
  transition: margin-left ${theme.transitions.base};
  width: calc(100% - ${props => (props.isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH)}px);
  min-width: 0;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding-top: 72px;
  }
`;

const TopBar = styled.div<{ isCollapsed?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 28px;
  background: ${theme.glass.light};
  backdrop-filter: ${theme.glass.blur};
  -webkit-backdrop-filter: ${theme.glass.blur};
  border-bottom: 1px solid ${theme.colors.cardBorder};
  box-shadow: ${theme.shadows.topbar};
  position: fixed;
  top: 0;
  right: 0;
  left: ${props => (props.isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH)}px;
  z-index: 100;
  height: ${theme.layout.topbarHeight}px;
  transition: left ${theme.transitions.base};

  @media (max-width: 768px) {
    left: 0;
    width: 100%;
    padding: 14px 20px;
  }
`;

const ToggleButton = styled.button`
  background: ${theme.colors.card};
  border: 1px solid ${theme.colors.cardBorder};
  color: ${theme.colors.textSecondary};
  font-size: 18px;
  cursor: pointer;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.radius.md};
  transition: all ${theme.transitions.fast};
  box-shadow: ${theme.shadows.xs};

  &:hover {
    background: rgba(255, 107, 107, 0.08);
    border-color: rgba(255, 107, 107, 0.3);
    color: ${theme.colors.primary};
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 6px 10px 6px 16px;
  border-radius: ${theme.radius.pill};
  border: 1px solid ${theme.colors.cardBorder};
  background: ${theme.colors.card};
  transition: all ${theme.transitions.fast};
  box-shadow: ${theme.shadows.xs};
  position: relative;

  &:hover {
    border-color: rgba(255, 107, 107, 0.35);
    box-shadow: ${theme.shadows.sm};
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.div`
  font-size: ${theme.typography.body};
  font-weight: 600;
  color: ${theme.colors.text};
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

const UserAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${theme.colors.accentGradient};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 700;
  box-shadow: ${theme.shadows.glowSoft};
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: ${theme.colors.card};
  border-radius: ${theme.radius.lg};
  border: 1px solid ${theme.colors.cardBorder};
  box-shadow: ${theme.shadows.lg};
  min-width: 200px;
  display: ${props => (props.isOpen ? 'block' : 'none')};
  z-index: 1000;
  overflow: hidden;
  animation: fadeIn 0.15s ease;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.body};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  text-align: left;

  &:hover {
    background: rgba(255, 107, 107, 0.06);
    color: ${theme.colors.primary};
  }

  svg {
    width: 16px;
    color: ${theme.colors.primary};
  }
`;

const ProfileCard = styled.div`
  background: ${theme.colors.card};
  border-radius: ${theme.radius.xl};
  padding: 0;
  box-shadow: ${theme.shadows.card};
  border: 1px solid ${theme.colors.cardBorder};
  width: 90%;
  max-width: ${theme.layout.contentMaxWidth}px;
  margin: 32px auto;
  overflow: hidden;
  transition: box-shadow ${theme.transitions.base};

  &:hover {
    box-shadow: ${theme.shadows.cardHover};
  }

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    margin: 20px auto;
  }
`;

const ProfileHeader = styled.div`
  background: ${theme.colors.accentGradient};
  color: white;
  padding: 20px 28px;
  display: flex;
  align-items: center;
  gap: 14px;

  svg {
    font-size: 22px;
    opacity: 0.95;
  }
`;

const ProfileTitle = styled.h1`
  margin: 0;
  font-size: ${theme.typography.h2};
  font-weight: 700;
  letter-spacing: -0.03em;
`;

const ProfileSection = styled.div`
  padding: 28px 32px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ProfileField = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const FieldLabel = styled.div`
  font-size: ${theme.typography.small};
  color: ${theme.colors.textMuted};
  margin-bottom: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const FieldValue = styled.div`
  font-size: ${theme.typography.body};
  color: ${theme.colors.text};
  padding: 14px 16px;
  background: ${theme.colors.bg};
  border-radius: ${theme.radius.md};
  border: 1px solid ${theme.colors.cardBorder};
  width: 100%;
  overflow-wrap: break-word;
  font-weight: 500;
  transition: border-color ${theme.transitions.fast};

  &:hover {
    border-color: rgba(255, 107, 107, 0.2);
  }
`;

const PageContainer = styled.div`
  padding: 0 24px;
  width: 100%;
  max-width: ${theme.layout.contentMaxWidth}px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const Profile: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel
  } = useLogoutHandler();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getProfile();
        setProfile(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const handleLogout = () => {
    handleLogoutClick();
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <DashboardContainer>
      <Sidebar
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onMobileClose={toggleMobileMenu}
      />

      <MainContent isCollapsed={isCollapsed}>
        <TopBar isCollapsed={isCollapsed}>
          <ToggleButton onClick={() => {
            if (window.innerWidth <= 768) {
              toggleMobileMenu();
            } else {
              setIsCollapsed(!isCollapsed);
            }
          }}>
            <FontAwesomeIcon icon={faBars} />
          </ToggleButton>
          <UserProfile ref={dropdownRef} onClick={() => setIsProfileOpen(!isProfileOpen)}>
            <UserInfo>
              <UserName>{profile?.name || ''}</UserName>
            </UserInfo>
            <UserAvatar>{profile?.name?.charAt(0) || ''}</UserAvatar>
            <UserDropdown isOpen={isProfileOpen}>
              <DropdownItem onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faUser} />
                Profile
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Logout
              </DropdownItem>
            </UserDropdown>
          </UserProfile>
        </TopBar>

        <PageContainer>
          {loading ? (
            <div>Loading...</div>
          ) : (
            <ProfileCard>
              <ProfileHeader>
                <FontAwesomeIcon icon={faUser} />
                <ProfileTitle>Profile Information</ProfileTitle>
              </ProfileHeader>

              <ProfileSection>
                <ProfileField>
                  <FieldLabel>User Name</FieldLabel>
                  <FieldValue>{profile?.name || 'N/A'}</FieldValue>
                </ProfileField>

                <ProfileField>
                  <FieldLabel>Email</FieldLabel>
                  <FieldValue>{profile?.email || 'N/A'}</FieldValue>
                </ProfileField>

                <ProfileField>
                  <FieldLabel>Phone</FieldLabel>
                  <FieldValue>{profile?.phone || 'N/A'}</FieldValue>
                </ProfileField>

                <ProfileField>
                  <FieldLabel>Member Since</FieldLabel>
                  <FieldValue>
                    {profile?.createdAt
                      ? new Date(profile.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </FieldValue>
                </ProfileField>
              </ProfileSection>
            </ProfileCard>
          )}
        </PageContainer>
        <Footer isCollapsed={isCollapsed} />
      </MainContent>

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </DashboardContainer>
  );
};

export default Profile; 